import { useUser } from '@/stores/useUser'
import type { ServicePeriodHistory } from '~/types/servicePeriod'
import { useUserPref } from '~/stores/useUserPref'
import { readCookieValue } from '../composables/api'

export const useUserService = defineStore('useUserService', {
  state: () => {
    return {
      serviceArray: [] as Array<userServicesAttributes | null>,
      dataCache: {} as any,
      // allplatform: {} as userServicesAttributes,
      // home: {} as userServicesAttributes,
      // currentServiceCate: null as ServiceCate | null,
      serverLists: {} as { [serverId: number]: any },
      _serviceCount: 0,
      currentServiceCate: null as ServiceCate | null,
    }
  },
  getters: {
    boughtServiceCate(): Array<ServiceCate | undefined> {
      return this.serviceArray?.map((s) => s?.cate) || []
    },
    usableServiceCate(): Array<ServiceCate | undefined> {
      return this.serviceArray?.filter((s) => s?.status === 'on').map((s) => s?.cate) || []
    },
    serviceCount(): number {
      return this.serviceArray?.length || this._serviceCount
    },
    currentService(): userServicesAttributes | null {
      return this?.[this.currentServiceCate as ServiceCate] || null
    },
    hasService(): boolean {
      return this.serviceCount > 0
    },
    // currentServiceCate(): ServiceCate | null {
    //   const pref = useUserPref().preferredServiceCate as ServiceCate
    //   console.log('read pref:', pref)
    //   const prefServiceUsable = this.usableServiceCate.includes(pref)
    //   const prefServiceBought = this.boughtServiceCate.includes(pref)
    //   if (prefServiceUsable) {
    //     return pref
    //   }
    //   // 买过，但是没开，优先显示
    //   if (prefServiceBought) {
    //     return pref
    //   }
    //   if (pref && this.usableServiceCate.includes(pref)) {
    //     // 当前有记录下偏好，且该服务有效
    //     return pref as ServiceCate
    //   }
    //   return (this.usableServiceCate?.[0] || this.boughtServiceCate?.[0]) as ServiceCate || null
    // },
    allplatform(): userServicesAttributes | null {
      return this.serviceArray?.find?.((s) => s?.cate === 'allplatform') || null
    },
    home(): userServicesAttributes | null {
      return this.serviceArray?.find?.((s) => s?.cate === 'home') || null
    },
  },
  actions: {
    async getCurrentUserService({ refresh = false }: { refresh?: boolean }) {
      const user = useUser()
      const userId = user.id!
      const cache = this.dataCache[userId]
      const now = new Date().valueOf()
      let data
      let cacheExpired
      if (!cache) {
        cacheExpired = true
      } else {
        cacheExpired = now - cache.time > 5 * 60 * 1000
        // console.log('found service cache, expired?', cacheExpired)
      }
      if (!refresh && !cacheExpired) {
        data = cache
        // console.log('use cached service data', data)
      } else {
        data = await useAPI().get(`users/${user.id}/services`)
        // console.log('use api service data', data.length)
        this.dataCache[userId] = data
      }
      this.serviceArray = data
      // if (useIsBrowser()) {
      // }
      this.setDefaultCurrentServiceCate()
      return data
    },
    setDefaultCurrentServiceCate() {
      // const pref = useUserPref().preferredServiceCate as ServiceCate
      const pref = readCookieValue('preferredServiceCate', '')
      // console.log('read pref:', pref)
      const prefServiceUsable = this.usableServiceCate.includes(pref)
      // const prefServiceBought = this.boughtServiceCate.includes(pref)
      if (pref && prefServiceUsable) {
        // 当前有记录下偏好，且该服务有效
        this.currentServiceCate = pref
      } else if (this.usableServiceCate.length > 0) {
        // 没有偏好/不再有效，那就切换到第一个可用的服务
        this.currentServiceCate = this.usableServiceCate[0]!
      } else if (this.boughtServiceCate.length > 0) {
        // 可用的服务也没有？那就默认切换到买过的第一个
        // 过了这个那就是啥都没有的了，那就没有默认的CurrentServiceCate了
        this.currentServiceCate = this.boughtServiceCate[0]!
      }
      return this.currentServiceCate
    },
    getTrafficLog({ serviceId, params }: { serviceId: number; params: any }) {
      return useAPI().get(`users/${useUser().id}/services/${serviceId}/traffic_log`, {
        params,
      })
    },
    getTrafficBreakdown({ serviceId, params }: { serviceId: number; params: any }) {
      return useAPI().get(`users/${useUser().id}/services/${serviceId}/traffic_breakdown`, {
        params,
      })
    },
    async getServers({ serviceId, reload = false }: { serviceId: number; reload?: boolean }) {
      // console.log('!!!!!----->server cache is', this.serverLists, 'cache for this query:', this.serverLists[serviceId])

      if (!reload && this.serverLists[serviceId]) {
        // console.log('returning cached serverList')
        return this.serverLists[serviceId]
      }
      // console.log('reading data from api...')

      const data = await useAPI().get(`users/${useUser().id}/services/${serviceId}/lines`)
      // console.log('read data:', data)

      this.serverLists[serviceId] = data
      return data
    },
    async updateServiceOpt({ serviceId, body }: { serviceId: number; body: any }) {
      const data = await useAPI().put(`users/${useUser().id}/services/${serviceId}`, { body })
      const appliedBody = data && typeof data === 'object'
        ? { ...body, ...(data as Record<string, unknown>) }
        : body
      this.currentService!.extra = { ...this.currentService!.extra, ...appliedBody }
      return data
    },
    async updateCustomEgress({ serviceId, body }: { serviceId: number; body: any }) {
      const data = await useAPI().put(`users/${useUser().id}/services/${serviceId}/custom_egress`, { body })
      if (data && typeof data === 'object') {
        this.currentService!.extra = {
          ...this.currentService!.extra,
          ...(data as Record<string, unknown>),
        }
      }
      return data
    },
    getAlreadyBoughtPremiumData(addonPlanId: number) {
      if (!Number.isInteger(addonPlanId) || addonPlanId <= 0) {
        throw new Error('Missing data add-on plan identifier.')
      }
      return useAPI().get(`users/${useUser().id}/services/pre_bought`, {
        params: { addon_plan_id: addonPlanId },
      }) as Promise<{ addon_plan_id: number, period_key: string, allplatform: number }>
    },
    changeBuylength({
      serviceId,
      body,
    }: {
      serviceId: number
      body: {
        buy_length: number
        turn_on: boolean
        set_quota: boolean
      }
    }) {
      return useAPI().put(`users/${useUser().id}/services/${serviceId}/buy_length`, { body })
    },
    resetServiceConfigInfo({ serviceId }: { serviceId: number }) {
      return useAPI().put(`users/${useUser().id}/services/${serviceId}/reset`)
    },
    getIPLog({ serviceId, params }: { serviceId: number; params: { duration: number } }) {
      // duration: 3600
      return useAPI().get(`users/${useUser().id}/services/${serviceId}/ss_ip_log`, { params }) as Promise<IPLogRecord[]>
    },
    getServicePeriods({ serviceId, limit = 12 }: { serviceId: number, limit?: number }) {
      return useAPI().get(`users/${useUser().id}/services/${serviceId}/billing_periods`, {
        params: { limit },
      }) as Promise<ServicePeriodHistory>
    },
    getMonthlyUsage({ serviceId }: { serviceId: number }) {
      return useAPI().get(`users/${useUser().id}/services/${serviceId}/monthly_usage`) as Promise<
        Array<MonthlyUsageRecord>
      >
    },
    async getServiceCount() {
      const data = (await useAPI().get(`users/${useUser().id}/service_count`)) as { count: number }
      this._serviceCount = data.count || 0
      return data.count
    },
    updateExtra(data: ServiceExtra) {
      Object.assign(this.currentService!.extra!, { ...this.currentService!.extra, ...data })
    },
  },
})
